const IV_BYTES = 16;
const OFFSET_BYTES = 8;

export function createShareKey(): Uint8Array<ArrayBuffer> {
  return crypto.getRandomValues(new Uint8Array(32));
}

export function shareKeyToString(key: Uint8Array): string {
  let binary = "";
  for (const byte of key) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function shareKeyFromString(value: string): Uint8Array<ArrayBuffer> {
  if (!/^[A-Za-z0-9_-]+$/.test(value)) throw new Error("The sharing link is malformed.");
  const base64 = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
  let binary: string;
  try {
    binary = atob(padded);
  } catch {
    throw new Error("The sharing link is malformed.");
  }
  const key = Uint8Array.from(binary, (character) => character.charCodeAt(0));
  if (key.byteLength !== 32) throw new Error("The sharing link contains an invalid key.");
  return key;
}

export function sharingKeyFromLocation(): Uint8Array<ArrayBuffer> | null {
  const match = location.hash.match(/^#share=([A-Za-z0-9_-]+)$/);
  return match ? shareKeyFromString(match[1]) : null;
}

export function sharingUrl(key: Uint8Array): string {
  const url = new URL(document.baseURI);
  url.hash = `share=${shareKeyToString(key)}`;
  return url.toString();
}

export async function importEncryptionKey(key: Uint8Array<ArrayBuffer>): Promise<CryptoKey> {
  return crypto.subtle.importKey("raw", key, { name: "AES-GCM" }, false, [
    "encrypt",
    "decrypt",
  ]);
}

/** Wire format retained from Peermonger: IV + 64-bit file offset + AES-GCM data. */
export async function encrypt(
  key: CryptoKey,
  plain: BufferSource,
  offset = 0,
): Promise<ArrayBuffer> {
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
  const additionalData = new Uint8Array(OFFSET_BYTES);
  new DataView(additionalData.buffer).setBigUint64(0, BigInt(offset), false);
  const cipher = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv, additionalData },
    key,
    plain,
  );
  const result = new Uint8Array(iv.byteLength + additionalData.byteLength + cipher.byteLength);
  result.set(iv, 0);
  result.set(additionalData, iv.byteLength);
  result.set(new Uint8Array(cipher), iv.byteLength + additionalData.byteLength);
  return result.buffer;
}

export async function decrypt(key: CryptoKey, message: ArrayBuffer): Promise<ArrayBuffer> {
  if (message.byteLength < IV_BYTES + OFFSET_BYTES + 16) {
    throw new Error("The peer sent an invalid encrypted message.");
  }
  return crypto.subtle.decrypt(
    {
      name: "AES-GCM",
      iv: new Uint8Array(message, 0, IV_BYTES),
      additionalData: new Uint8Array(message, IV_BYTES, OFFSET_BYTES),
    },
    key,
    new Uint8Array(message, IV_BYTES + OFFSET_BYTES),
  );
}

export function messageOffset(message: ArrayBuffer): number {
  const value = new DataView(message, IV_BYTES, OFFSET_BYTES).getBigUint64(0, false);
  const offset = Number(value);
  if (!Number.isSafeInteger(offset)) throw new Error("The peer sent an invalid file offset.");
  return offset;
}

export async function messageArrayBuffer(data: unknown): Promise<ArrayBuffer> {
  if (data instanceof ArrayBuffer) return data;
  if (ArrayBuffer.isView(data)) {
    return data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength) as ArrayBuffer;
  }
  if (data instanceof Blob) return data.arrayBuffer();
  throw new Error("The peer sent an unsupported message type.");
}
